/**
 * Account Settings Modal
 * Shows user profile, subscription, usage stats, and settings
 */

import { authService } from './auth-service.js';
import { SUPABASE_URL } from '../config/config.js';
import { checkoutService } from '../payments/checkout-service.js';
import { escapeHtml } from '../lib/html-utils.js';
import { showNotification } from '../lib/notification-utils.js';

class AccountSettings {
  constructor() {
    this.modal = null;
    this.isOpen = false;
    this.stats = null;
    this.freeMode = false;
  }

  /**
   * Initialize the account settings modal
   */
  init() {
    this.createModal();
    this.attachEventListeners();
  }

  /**
   * Create the modal HTML
   */
  createModal() {
    const modalHTML = `
      <div id="accountSettingsModal" class="account-modal" style="display: none;">
        <div class="account-modal-backdrop"></div>
        <div class="account-modal-content">
          <button class="account-modal-close">&times;</button>

          <h2 class="account-title">Account Settings</h2>

          <div class="account-tabs">
            <button class="account-tab active" data-tab="profile">Profile</button>
            <button class="account-tab" data-tab="subscription">Subscription</button>
            <button class="account-tab" data-tab="usage">Usage</button>
            <button class="account-tab" data-tab="security">Security</button>
          </div>

          <!-- Profile Tab -->
          <div class="account-tab-content active" id="tab-profile">
            <div class="account-section">
              <h3>Profile Information</h3>
              <form id="profileForm" class="account-form">
                <div class="account-form-group">
                  <label>Email</label>
                  <input type="email" id="profileEmail" disabled>
                </div>
                <div class="account-form-group">
                  <label for="profileDisplayName">Display Name</label>
                  <input type="text" id="profileDisplayName" placeholder="Your display name" maxlength="100">
                </div>
                <div class="account-form-actions">
                  <button type="submit" class="btn btn-primary btn-small">Save Changes</button>
                  <span class="account-save-status" id="profileSaveStatus"></span>
                </div>
              </form>
            </div>
          </div>

          <!-- Subscription Tab -->
          <div class="account-tab-content" id="tab-subscription">
            <div class="account-section">
              <h3>Current Plan</h3>
              <div class="account-plan-info">
                <div class="account-plan-badge" id="accountPlanBadge">FREE</div>
                <div class="account-plan-details">
                  <p id="accountPlanStatus">Active</p>
                  <p id="accountPlanRenewal" style="display: none;"></p>
                </div>
              </div>
              <div class="account-plan-actions">
                <button class="btn btn-primary btn-small" id="accountUpgradeBtn">Upgrade Plan</button>
                <button class="btn btn-small" id="accountManageBtn" style="display: none;">Manage Subscription</button>
              </div>
            </div>

            <div class="account-section">
              <h3>Credits</h3>
              <div class="account-credits-info">
                <div class="account-credits-balance">
                  <span class="credits-label">Current Balance</span>
                  <span class="credits-value" id="accountCreditsBalance">0</span>
                </div>
                <div class="account-credits-lifetime">
                  <span class="credits-label">Lifetime Purchased</span>
                  <span class="credits-value" id="accountCreditsLifetime">0</span>
                </div>
              </div>
              <button class="btn btn-small" id="accountBuyCreditsBtn">Buy More Credits</button>
            </div>
          </div>

          <!-- Usage Tab -->
          <div class="account-tab-content" id="tab-usage">
            <div class="account-section">
              <h3>This Month</h3>
              <div class="account-usage-stats">
                <div class="usage-stat">
                  <span class="usage-stat-value" id="usageMonthExports">0</span>
                  <span class="usage-stat-label">Exports</span>
                </div>
                <div class="usage-stat">
                  <span class="usage-stat-value" id="usageMonthMinutes">0</span>
                  <span class="usage-stat-label">Minutes</span>
                </div>
              </div>
            </div>

            <div class="account-section">
              <h3>All Time</h3>
              <div class="account-usage-stats">
                <div class="usage-stat">
                  <span class="usage-stat-value" id="usageAllTimeExports">0</span>
                  <span class="usage-stat-label">Exports</span>
                </div>
                <div class="usage-stat">
                  <span class="usage-stat-value" id="usageAllTimeMinutes">0</span>
                  <span class="usage-stat-label">Minutes</span>
                </div>
              </div>
            </div>

            <div class="account-section">
              <h3>Recent Exports</h3>
              <div class="account-recent-exports" id="accountRecentExports">
                <div class="account-loading">Loading...</div>
              </div>
            </div>
          </div>

          <!-- Security Tab -->
          <div class="account-tab-content" id="tab-security">
            <div class="account-section">
              <h3>Change Password</h3>
              <form id="passwordForm" class="account-form">
                <div class="account-form-group">
                  <label for="newPassword">New Password</label>
                  <input type="password" id="newPassword" placeholder="New password" minlength="6" required>
                </div>
                <div class="account-form-group">
                  <label for="confirmPassword">Confirm Password</label>
                  <input type="password" id="confirmPassword" placeholder="Confirm new password" required>
                </div>
                <div class="account-error" id="passwordError"></div>
                <div class="account-form-actions">
                  <button type="submit" class="btn btn-primary btn-small">Update Password</button>
                  <span class="account-save-status" id="passwordSaveStatus"></span>
                </div>
              </form>
            </div>

            <div class="account-section account-danger-zone">
              <h3>Danger Zone</h3>
              <p>Once you delete your account, there is no going back. Please be certain.</p>
              <button class="btn btn-danger btn-small" id="accountDeleteBtn">Delete Account</button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('accountSettingsModal');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Close modal
    this.modal.querySelector('.account-modal-backdrop').addEventListener('click', () => this.hide());
    this.modal.querySelector('.account-modal-close').addEventListener('click', () => this.hide());

    // Tab switching
    this.modal.querySelectorAll('.account-tab').forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab.dataset.tab));
    });

    // Profile form
    document.getElementById('profileForm')?.addEventListener('submit', (e) => this.handleProfileSave(e));

    // Password form
    document.getElementById('passwordForm')?.addEventListener('submit', (e) => this.handlePasswordChange(e));

    // Subscription buttons
    document.getElementById('accountUpgradeBtn')?.addEventListener('click', () => {
      this.hide();
      // Trigger pricing modal via callback
      if (this.onUpgradeClick) this.onUpgradeClick();
    });

    document.getElementById('accountManageBtn')?.addEventListener('click', () => this.openCustomerPortal());

    document.getElementById('accountBuyCreditsBtn')?.addEventListener('click', () => {
      this.hide();
      if (this.onUpgradeClick) this.onUpgradeClick();
    });

    // Delete account
    document.getElementById('accountDeleteBtn')?.addEventListener('click', () => this.handleDeleteAccount());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    });
  }

  /**
   * Switch to a tab
   */
  switchTab(tabId) {
    // Update tab buttons
    this.modal.querySelectorAll('.account-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.tab === tabId);
    });

    // Update tab content
    this.modal.querySelectorAll('.account-tab-content').forEach(content => {
      content.classList.toggle('active', content.id === `tab-${tabId}`);
    });
  }

  /**
   * Set Free Mode state
   * When Free Mode is active, hides subscription and usage tabs
   * @param {boolean} freeMode - Whether Free Mode is active
   */
  setFreeMode(freeMode) {
    this.freeMode = freeMode;
    // Update tab visibility if modal exists
    if (this.modal) {
      const subscriptionTab = this.modal.querySelector('.account-tab[data-tab="subscription"]');
      const usageTab = this.modal.querySelector('.account-tab[data-tab="usage"]');
      if (subscriptionTab) subscriptionTab.style.display = freeMode ? 'none' : '';
      if (usageTab) usageTab.style.display = freeMode ? 'none' : '';
    }
  }

  /**
   * Show the modal
   */
  async show() {
    this.modal.style.display = 'flex';
    this.isOpen = true;
    // Apply Free Mode tab visibility
    this.setFreeMode(this.freeMode);
    // If Free Mode is active and currently on a hidden tab, switch to profile
    if (this.freeMode) {
      const activeTab = this.modal.querySelector('.account-tab.active');
      if (activeTab && (activeTab.dataset.tab === 'subscription' || activeTab.dataset.tab === 'usage')) {
        this.switchTab('profile');
      }
    }
    await this.loadStats();
  }

  /**
   * Hide the modal
   */
  hide() {
    this.modal.style.display = 'none';
    this.isOpen = false;
  }

  /**
   * Load account stats
   */
  async loadStats() {
    if (!authService.isAuthenticated()) return;

    try {
      const accessToken = authService.getAccessToken();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/get-usage-stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load stats');
      }

      this.stats = await response.json();
      this.renderStats();

    } catch (error) {
      console.error('Error loading account stats:', error);
    }
  }

  /**
   * Render stats in the UI
   */
  renderStats() {
    if (!this.stats) return;

    // Profile
    document.getElementById('profileEmail').value = this.stats.profile.email || '';
    document.getElementById('profileDisplayName').value = this.stats.profile.displayName || '';

    // Subscription - use tierInfo from server (dynamic) with fallback
    const tier = this.stats.subscription.tier;
    const tierInfo = this.stats.subscription.tierInfo || {
      displayName: tier,
      badgeLabel: tier.toUpperCase(),
      isPaid: false
    };
    const isPaidTier = tierInfo.isPaid;

    document.getElementById('accountPlanBadge').textContent = tierInfo.badgeLabel;
    document.getElementById('accountPlanBadge').className = `account-plan-badge tier-${tier}`;

    const status = this.stats.subscription.status;
    document.getElementById('accountPlanStatus').textContent =
      status === 'active' ? 'Active' :
      status === 'past_due' ? 'Payment Past Due' :
      status === 'canceled' ? 'Canceled' : status;

    if (this.stats.subscription.currentPeriodEnd && isPaidTier) {
      const renewalDate = new Date(this.stats.subscription.currentPeriodEnd).toLocaleDateString();
      const renewalEl = document.getElementById('accountPlanRenewal');
      renewalEl.style.display = 'block';
      renewalEl.textContent = this.stats.subscription.cancelAtPeriodEnd
        ? `Cancels on ${renewalDate}`
        : `Renews on ${renewalDate}`;
    }

    // Show manage button for paid plans
    if (isPaidTier && this.stats.subscription.stripeCustomerId) {
      document.getElementById('accountManageBtn').style.display = 'inline-block';
    }

    // Credits
    document.getElementById('accountCreditsBalance').textContent = this.stats.credits.balance;
    document.getElementById('accountCreditsLifetime').textContent = this.stats.credits.lifetimePurchased;

    // Usage
    document.getElementById('usageMonthExports').textContent = this.stats.usage.currentMonth.exportsCount;
    document.getElementById('usageMonthMinutes').textContent = this.stats.usage.currentMonth.exportsMinutes;
    document.getElementById('usageAllTimeExports').textContent = this.stats.usage.allTime.exportsCount;
    document.getElementById('usageAllTimeMinutes').textContent = this.stats.usage.allTime.exportsMinutes;

    // Recent exports
    this.renderRecentExports();
  }

  /**
   * Render recent exports list
   */
  renderRecentExports() {
    const container = document.getElementById('accountRecentExports');

    if (!this.stats.recentExports || this.stats.recentExports.length === 0) {
      container.innerHTML = '<div class="account-empty">No exports yet</div>';
      return;
    }

    const exportsHTML = this.stats.recentExports.map(exp => {
      const date = new Date(exp.createdAt).toLocaleDateString();
      const duration = Math.round(exp.durationSeconds / 60 * 10) / 10;
      return `
        <div class="recent-export-item">
          <div class="recent-export-info">
            <span class="recent-export-type">${escapeHtml(exp.visualizerType)}</span>
            <span class="recent-export-meta">${escapeHtml(exp.resolution)} - ${duration} min</span>
          </div>
          <span class="recent-export-date">${date}</span>
        </div>
      `;
    }).join('');

    container.innerHTML = exportsHTML;
  }

  /**
   * Handle profile save
   */
  async handleProfileSave(e) {
    e.preventDefault();

    const displayName = document.getElementById('profileDisplayName').value.trim();
    const statusEl = document.getElementById('profileSaveStatus');

    try {
      statusEl.textContent = 'Saving...';
      statusEl.className = 'account-save-status';

      const accessToken = authService.getAccessToken();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/update-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ displayName })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save');
      }

      statusEl.textContent = 'Saved!';
      statusEl.className = 'account-save-status success';

      setTimeout(() => {
        statusEl.textContent = '';
      }, 3000);

    } catch (error) {
      console.error('Profile save error:', error);
      statusEl.textContent = 'Error saving';
      statusEl.className = 'account-save-status error';
    }
  }

  /**
   * Handle password change
   */
  async handlePasswordChange(e) {
    e.preventDefault();

    const newPassword = document.getElementById('newPassword').value;
    const confirmPassword = document.getElementById('confirmPassword').value;
    const errorEl = document.getElementById('passwordError');
    const statusEl = document.getElementById('passwordSaveStatus');

    errorEl.textContent = '';

    if (newPassword !== confirmPassword) {
      errorEl.textContent = 'Passwords do not match';
      return;
    }

    if (newPassword.length < 6) {
      errorEl.textContent = 'Password must be at least 6 characters';
      return;
    }

    try {
      statusEl.textContent = 'Updating...';
      statusEl.className = 'account-save-status';

      await authService.updatePassword(newPassword);

      statusEl.textContent = 'Password updated!';
      statusEl.className = 'account-save-status success';

      // Clear form
      document.getElementById('newPassword').value = '';
      document.getElementById('confirmPassword').value = '';

      setTimeout(() => {
        statusEl.textContent = '';
      }, 3000);

    } catch (error) {
      console.error('Password change error:', error);
      errorEl.textContent = error.message || 'Failed to update password';
    }
  }

  /**
   * Open Stripe Customer Portal
   */
  async openCustomerPortal() {
    try {
      await checkoutService.openCustomerPortal();
    } catch (error) {
      console.error('Customer portal error:', error);
      alert('Failed to open customer portal: ' + error.message);
    }
  }

  /**
   * Handle account deletion
   * Requires the user to type "DELETE" to confirm, then calls the
   * delete-account Edge Function which removes all user data.
   */
  async handleDeleteAccount() {
    const confirmation = prompt(
      'This will permanently delete your account and all data:\n' +
      '- Your profile and settings\n' +
      '- Your subscription (no refund)\n' +
      '- Your remaining credits\n' +
      '- Your export history\n' +
      '- Your saved visualizers\n\n' +
      'This action cannot be undone.\n\n' +
      'Type DELETE to confirm:'
    );

    if (confirmation !== 'DELETE') return;

    try {
      const accessToken = authService.getAccessToken();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.error || 'Account deletion failed');
      }

      await authService.signOut();
      window.location.reload();
    } catch (error) {
      console.error('Delete account error:', error);
      showNotification('Failed to delete account: ' + error.message, { type: 'error' });
    }
  }
}

// Export singleton instance
export const accountSettings = new AccountSettings();
